import { DebugBox } from "./DebugBox";
import { formatCurrency } from "../lib/formatters";
import { formatForeignCurrency } from "../lib/utils";

interface IncomeCalculationsDebugProps {
  mortgageCalculations: any;
}

export function IncomeCalculationsDebug({ mortgageCalculations }: IncomeCalculationsDebugProps) {
  if (!mortgageCalculations) {
    return (
      <DebugBox title="Income Calculations" color="green">
        <div className="bg-white dark:bg-gray-700 p-3 rounded border border-gray-200 dark:border-gray-600 text-green-700 dark:text-green-300">
          Loading mortgage calculations...
        </div>
      </DebugBox>
    );
  }

  return (
    <DebugBox title="Income Calculations" color="green">
      <p className="text-green-700 dark:text-green-300 text-sm mb-3">
        Real-time income calculation variables based on current form inputs
      </p>
      
      <div className="space-y-4">
        {/* Foreign Income Stressed Calculation */}
        <div className="bg-white dark:bg-gray-700 p-4 rounded border border-gray-200 dark:border-gray-600">
          <h5 className="font-subheader text-green-800 dark:text-green-300 mb-3">Foreign Income - Stressed</h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-900 dark:text-gray-100">
            <div>
              <strong>Original Foreign Income:</strong><br />
              <span className="font-mono">{formatForeignCurrency(mortgageCalculations.calculationDetails.foreignIncome.originalAmount, mortgageCalculations.calculationDetails.foreignIncome.currency)}</span>
            </div>
            <div>
              <strong>Currency:</strong><br />
              <span className="font-mono">{mortgageCalculations.calculationDetails.foreignIncome.currency}</span>
            </div>
            <div>
              <strong>Stress Rate Applied:</strong><br />
              <span className="font-mono">{(mortgageCalculations.calculationDetails.foreignIncome.stressRate * 100).toFixed(2)}%</span>
            </div>
            <div>
              <strong>Stressed Amount:</strong><br />
              <span className="font-mono text-green-700 dark:text-green-300 font-subheader">{formatCurrency(mortgageCalculations.calculations.foreignIncomeStressed)}</span>
            </div>
          </div>
          <div className="mt-3 p-2 bg-green-100 dark:bg-green-900/30 rounded text-xs text-gray-900 dark:text-gray-100">
            <strong>Formula:</strong> <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">{mortgageCalculations.calculationDetails.foreignIncome.formula}</code>
          </div>
        </div>

        {/* Income from Properties Stressed Calculation */}
        <div className="bg-white dark:bg-gray-700 p-4 rounded border border-gray-200 dark:border-gray-600">
          <h5 className="font-subheader text-green-800 dark:text-green-300 mb-3">Income from Properties - Stressed</h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-900 dark:text-gray-100">
            <div>
              <strong>Original Income from Properties:</strong><br />
              <span className="font-mono">{formatCurrency(mortgageCalculations.calculationDetails.incomeFromProperties.originalAmount)}</span>
            </div>
            <div>
              <strong>BTL Income Reduced for Voids:</strong><br />
              <span className="font-mono">{mortgageCalculations.calculationDetails.incomeFromProperties.btlIncomeReducedForVoids}</span>
            </div>
            <div>
              <strong>Stressed Amount:</strong><br />
              <span className="font-mono text-green-700 dark:text-green-300 font-subheader">{formatCurrency(mortgageCalculations.calculations.incomeFromPropertiesStressed)}</span>
            </div>
          </div>
          <div className="mt-3 p-2 bg-green-100 dark:bg-green-900/30 rounded text-xs text-gray-900 dark:text-gray-100">
            <strong>Formula:</strong> <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">{mortgageCalculations.calculationDetails.incomeFromProperties.formula}</code>
          </div>
        </div>

        {/* Total Income Calculation */}
        <div className="bg-white dark:bg-gray-700 p-4 rounded border border-gray-200 dark:border-gray-600">
          <h5 className="font-subheader text-green-800 dark:text-green-300 mb-3">Total Income</h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-900 dark:text-gray-100">
            <div>
              <strong>Total Income Net of Tax:</strong><br />
              <span className="font-mono">{formatCurrency(mortgageCalculations.calculationDetails.totalIncome.totalIncomeNetOfTax)}</span>
            </div>
            <div>
              <strong>Foreign Income (Stressed):</strong><br />
              <span className="font-mono">{formatCurrency(mortgageCalculations.calculationDetails.totalIncome.foreignIncomeStressed)}</span>
            </div>
            <div>
              <strong>Properties Income (Stressed):</strong><br />
              <span className="font-mono">{formatCurrency(mortgageCalculations.calculationDetails.totalIncome.incomeFromPropertiesStressed)}</span>
            </div>
            <div>
              <strong>Total Combined Income:</strong><br />
              <span className="font-mono text-green-700 dark:text-green-300 font-subheader">{formatCurrency(mortgageCalculations.calculations.totalIncome)}</span>
            </div>
          </div>
          <div className="mt-3 p-2 bg-green-100 dark:bg-green-900/30 rounded text-xs text-gray-900 dark:text-gray-100">
            <strong>Formula:</strong> <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">{mortgageCalculations.calculationDetails.totalIncome.formula}</code>
          </div>
        </div>

        {/* Monthly Income Calculation */}
        <div className="bg-white dark:bg-gray-700 p-4 rounded border border-gray-200 dark:border-gray-600">
          <h5 className="font-subheader text-green-800 dark:text-green-300 mb-3">Monthly Income</h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-900 dark:text-gray-100">
            <div>
              <strong>Total Annual Income:</strong><br />
              <span className="font-mono">{formatCurrency(mortgageCalculations.calculationDetails.monthlyIncome.totalIncome)}</span>
            </div>
            <div>
              <strong>Monthly Income:</strong><br />
              <span className="font-mono text-green-700 dark:text-green-300 font-subheader">{formatCurrency(mortgageCalculations.calculations.monthlyIncome)}</span>
            </div>
          </div>
          <div className="mt-3 p-2 bg-green-100 dark:bg-green-900/30 rounded text-xs text-gray-900 dark:text-gray-100">
            <strong>Formula:</strong> <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">{mortgageCalculations.calculationDetails.monthlyIncome.formula}</code>
          </div>
        </div>

        {/* Summary */}
        <div className="bg-green-100 dark:bg-green-900/30 p-3 rounded">
          <h5 className="font-subheader text-green-800 dark:text-green-300 mb-2">Income Calculation Summary</h5>
          <div className="text-sm space-y-1 text-gray-900 dark:text-gray-100">
            <div><strong>Total Income:</strong> {formatCurrency(mortgageCalculations.calculations.totalIncome)}</div>
            <div><strong>Monthly Income:</strong> {formatCurrency(mortgageCalculations.calculations.monthlyIncome)}</div>
          </div>
        </div>
      </div>
    </DebugBox>
  );
}
